// libraries
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import Dinero from 'dinero.js'

// state
import { clearCustomer } from 'state/customer'
import { clearTransportationInformation } from 'state/inboundTicket'
import { clearPurchaseOrderFilters } from 'state/purchaseOrder'

// queries
import { useGetInboundTicketsQuery } from 'services/inboundTickets'

// components
import InboundTicketsGridView from 'components/containers/inbound-tickets/components/InboundTicketsGridView'
import InboundTicketsListView from 'components/generic/datagrid/InboundTicketsListView'

// helpers and utils
import {
  TICKET_STATUSES,
  SORTABLE_IBT_FIELDS,
  IBT_LIBRARY_VIEW_OPTIONS,
  CURRENCY_PREPENDS,
  PAGE_SIZE,
} from 'utils/constants'
import { setDecimalPrecision, buildFilter } from 'utils/helpers'
import { useViewModeToggle } from 'utils/custom-hooks/useViewModeToggle'
import { useSelectFilter } from 'utils/custom-hooks/useSelectFilter'
import useRenderDateRangePicker from 'utils/custom-hooks/useRenderDateRangePicker'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'

const InboundTickets = () => {
  const dispatch = useDispatch()
  const { showIbtGridView, showIbtStatusSelect, currencyExchangeRatesFF } = useSelector(
    state => state.featureFlags?.variables || {},
  )
  const { viewMode, renderViewModeToggle } = useViewModeToggle({ localStorageKey: 'inboundTicketLibraryViewMode' })
  const { renderDateRangePicker, startDate, endDate } = useRenderDateRangePicker()
  const { allowedOptions, renderSelectFilter } = useSelectFilter({
    localStorageKey: 'inboundTicketLibraryStatuses',
    options: [
      TICKET_STATUSES.INCOMPLETE,
      TICKET_STATUSES.PRICE_COMPLETE,
      TICKET_STATUSES.APPROVED,
      TICKET_STATUSES.SCALE_COMPLETE,
      TICKET_STATUSES.PAID,
      TICKET_STATUSES.VOID,
      TICKET_STATUSES.TRUCK_IN_YARD,
    ],
    defaultOptions: [TICKET_STATUSES.INCOMPLETE.id, TICKET_STATUSES.SCALE_COMPLETE.id],
    label: 'Status',
  })

  const { defaultCurrency } = useSelector(state => state.settings)
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const { showWeightDecimals } = useSelector(state => state.settings?.settings?.purchaseSettings?.inboundTickets || {})
  const { paginateIBTs } = useSelector(state => state.featureFlags?.variables || {})

  const [queryOptions, setQueryOptions] = useState({
    filter: {
      sort: {
        field: SORTABLE_IBT_FIELDS.TICKET_ID,
        ascending: false,
      },
      statuses: allowedOptions,
      search: '',
      ...JSON.parse(buildFilter(startDate, endDate, defaultYardTimezone)),
    },
    page: 1,
    pageSize: viewMode === IBT_LIBRARY_VIEW_OPTIONS.LIST ? PAGE_SIZE : 0,
  })

  const { data: inboundTicketList, isFetching } = useGetInboundTicketsQuery(
    {
      ...(paginateIBTs && queryOptions),
      filter: JSON.stringify(queryOptions.filter),
    },
    { skip: !queryOptions.filter?.sort?.field || queryOptions.pageSize === 0 },
  )

  useGlobalLoader(false)

  useEffect(() => {
    setQueryOptions(prevQuery => ({
      ...prevQuery,
      filter: {
        ...prevQuery.filter,
        date: undefined, // clear start date before applying buildFilter
        endDate: undefined, // clear end date before applying buildFilter
        ...JSON.parse(buildFilter(startDate, endDate, defaultYardTimezone)),
      },
    }))
  }, [startDate, endDate, defaultYardTimezone])

  useEffect(() => {
    setQueryOptions(prevQuery => ({
      ...prevQuery,
      filter: {
        ...prevQuery.filter,
        statuses: allowedOptions,
      },
    }))
  }, [allowedOptions])

  useEffect(() => {
    dispatch(clearCustomer())
    dispatch(clearTransportationInformation())
    dispatch(clearPurchaseOrderFilters())
  }, [])

  const { defaultYardUm } = useSelector(state => state.settings)

  const transformedRows = (inboundTicketList?.Items || []).slice().map(ticket => {
    const ticketCurrency = ticket?.netValue?.currency
    const pricePrepend =
      !ticketCurrency || defaultCurrency === ticketCurrency
        ? CURRENCY_PREPENDS[defaultCurrency]?.hc
        : CURRENCY_PREPENDS[ticketCurrency]?.fc
    return {
      ...ticket,
      key: ticket.id,
      ticketLink: `/app/purchases/ticket-library/${ticket.id}`,
      ticketStatus: TICKET_STATUSES[ticket.status?.value]?.name,
      ticketEffectiveDate: ticket.effectiveDate ? new Date(ticket.effectiveDate) : undefined,
      customerIdentifierString: ticket.customer?.customerCommonIdentifierString,
      truckNumber: ticket.transportationInfo?.carrierNumber,
      trailerNumber: ticket.transportationInfo?.trailerNumber,
      billOfLading: ticket.transportationInfo?.billOfLading,
      grossWeight: `${setDecimalPrecision(ticket.loadWeight?.gross, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      })} ${defaultYardUm}`,
      tareWeight: `${setDecimalPrecision(ticket.loadWeight?.tare, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      })} ${defaultYardUm}`,
      netWeight: `${setDecimalPrecision(ticket.netWeight, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      })} ${defaultYardUm}`,
      netValue: Dinero(ticket?.netValue).toUnit() + (ticket?.feeOrPromotionTotal || 0),
      pricePrepend: currencyExchangeRatesFF ? pricePrepend : '$',
    }
  })

  const sharedProps = {
    useBackendSortAndFilter: true,
    filter: queryOptions?.filter,
    setFilter: newFilter => setQueryOptions(prevState => ({ ...prevState, filter: newFilter })),
    renderViewModeToggle: showIbtGridView ? renderViewModeToggle : () => null,
    renderDateRangePicker,
    loading: isFetching,
    rows: transformedRows,
    allowedOptions,
    renderSelectFilter: showIbtStatusSelect ? renderSelectFilter : () => null,
    selectFilterPath: showIbtStatusSelect ? 'status.value' : '',
    ...(paginateIBTs && {
      rowCount: inboundTicketList?.totalResultsCount || 0,
      paginationMode: 'server',
      useBackendSortAndFilter: true,
      onPageChange: newPageNumber => setQueryOptions(prevState => ({ ...prevState, page: newPageNumber + 1 })),
      onPageSizeChange: newPageSize => setQueryOptions(prevState => ({ ...prevState, pageSize: newPageSize })),
      setQueryOptions,
      autoPageSize: false,
      pageSize: queryOptions.pageSize,
    }),
  }

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
      {viewMode === IBT_LIBRARY_VIEW_OPTIONS.LIST ? (
        <InboundTicketsListView {...sharedProps} />
      ) : (
        <InboundTicketsGridView {...sharedProps} />
      )}
    </Grid>
  )
}

export default InboundTickets
